import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  Slide,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Fight } from '../../../../../../corelogic/domain/models/fight.model';
import { Fighter } from '../../../../../../corelogic/domain/models/fighter.model';
import { FighterInfo } from '../../../../../../corelogic/domain/models/fighterInfo.model';
import { RecentFightResult } from '../../../../../../corelogic/domain/models/recentFightResult.model';
import { SeasonFights } from '../../../../../../corelogic/domain/models/seasonFights.model';
import { capitalizeFirstLetter } from '../../../../../../corelogic/domain/utils/capitalize.utils';
import {
  calculateAge,
  convertToFeetInches,
  convertToInches,
} from '../../../../../../corelogic/domain/utils/fighter.utils';
import { useFighterImageState } from '../../../hooks/useFighterImage';
import { FighterImageService } from '../../../services/FighterImage.service';
import FightResultCircle from '../../Shared/FightResultCircle/FightResultCircle';

interface FighterDetailsProps {
  open: boolean;
  onClose: () => void;
  fighterSeasonInfo: FighterInfo | null;
  fighter: Fighter | null;
  seasonFight: SeasonFights | null;
  recentFightResults: RecentFightResult[] | null;
  fight: Fight | null;
}

const FighterDetails: React.FC<FighterDetailsProps> = ({
  open,
  onClose,
  fighterSeasonInfo,
  fighter,
  seasonFight,
  recentFightResults,
  fight,
}) => {
  const [isSliding, setIsSliding] = useState(true);

  const { fighterImage, isLoading } = useFighterImageState(
    fighter?.firstname,
    fighter?.lastname,
    fighter?.dob,
  );

  // Request fighter image when component mounts or fighter changes
  useEffect(() => {
    if (fighter) {
      FighterImageService.retrieveFighterImage(
        fighter.firstname,
        fighter.lastname,
        fighter.dob,
        fighter.gender,
      );
    }
  }, [fighter]);

  // Early return if no fighter - AFTER all hooks are called
  if (!fighterSeasonInfo || !fighter) {
    return null;
  }

  // Common styles
  const sectionContainerStyle = {
    marginBottom: '20px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  };

  const sectionHeaderStyle = {
    fontFamily: 'Rubik Mono One',
    color: '#cc0000',
    fontSize: {
      xs: '0.9rem',
      sm: 'clamp(0.9rem, 1.2vw, 1.2rem)',
    },
    borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
    paddingBottom: '5px',
    marginBottom: '10px',
    width: '100%',
    textTransform: 'uppercase',
    textAlign: 'center',
  };

  const sectionTextStyle = {
    fontSize: { xs: '0.9rem', sm: 'clamp(0.6rem, 1vw, 0.8rem)' },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    padding: '3px 0',
  };

  const detailLabelStyle = {
    fontSize: { xs: '0.9rem', sm: 'clamp(0.6rem, 1vw, 0.8rem)' },
    color: 'rgba(255, 255, 255, 0.7)',
    width: '43%',
    textTransform: 'uppercase',
    textAlign: 'left',
    paddingRight: '12px',
  };

  const detailValueStyle = {
    flex: 1,
    textAlign: 'left',
    fontSize: { xs: '1rem', sm: 'clamp(0.8rem, 1.2vw, 1rem)' },
  };

  const handleClose = () => {
    setIsSliding(false);
    setTimeout(() => {
      onClose();
      setIsSliding(true);
    }, 300);
  };

  const SectionHeader = ({ title }: { title: string }) => (
    <Typography variant="body1" sx={sectionHeaderStyle}>
      {title}
    </Typography>
  );

  return (
    <Slide
      direction="up"
      in={open && isSliding}
      mountOnEnter
      unmountOnExit
      timeout={300}
    >
      <Box
        sx={{
          width: '100%',
          height: 'calc(100vh - 100px)',
          bgcolor: 'rgba(20, 20, 20, 0.9)',
          border: '2px solid white',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          color: 'white',
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: '8px',
            top: '8px',
            color: 'white',
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Content Container - Centers all content */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: '20px',
            textAlign: 'center',
          }}
        >
          {/* Fighter Name */}
          <Typography
            variant="body1"
            sx={{
              fontFamily: 'Rubik Mono One',
              textAlign: 'center',
              fontSize: { xs: '1rem', sm: 'clamp(0.8rem, 1.5vw, 1.2rem)' },
              backgroundColor: '#8B0000',
              padding: '10px 20px',
              width: '100%',
            }}
          >
            {fighterSeasonInfo.FighterName}
          </Typography>

          {/* Fighter Image */}
          <Box
            sx={{
              width: '100%',
              height: { xs: '12%', sm: '25%' },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: { xs: '5px', sm: '10px' },
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  width: { xs: 80, sm: 120 },
                  height: { xs: 80, sm: 120 },
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CircularProgress
                  size={40}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                  }}
                />
              </Box>
            ) : fighterImage ? (
              <Box
                component="img"
                src={fighterImage}
                alt={`${fighter.firstname} ${fighter.lastname}`}
                sx={{
                  maxWidth: { xs: '45%', sm: '80%' },
                  maxHeight: { xs: '120px', sm: '100%' },
                  objectFit: 'contain',
                  borderRadius: '8px',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: { xs: 80, sm: 120 },
                  height: { xs: 80, sm: 120 },
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                <Typography
                  sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8rem' }}
                >
                  No Image
                </Typography>
              </Box>
            )}
          </Box>

          {/* Rest of the details in remaining height */}
          <Box
            sx={{
              width: '100%',
              height: { xs: '80%', sm: '70%' },
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-evenly',
              padding: '20px',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.4)',
                },
              },
            }}
          >
            {/* Fighter Details */}
            <Box sx={{ ...sectionContainerStyle }}>
              <SectionHeader title="Record" />
              <Typography
                sx={{
                  ...sectionTextStyle,
                  fontWeight: 'bold',
                  fontSize: {
                    xs: '0.9rem',
                    sm: 'clamp(0.9rem, 1.2vw, 1.5rem)',
                  },
                  textAlign: 'center',
                  width: '100%',
                  justifyContent: 'center',
                  display: 'flex',
                }}
              >
                {fighter.wins}-{fighter.losses}-{fighter.draws}
              </Typography>
            </Box>

            <Box sx={{ ...sectionContainerStyle }}>
              <SectionHeader title="Details" />
              <Box
                sx={{
                  width: '90%',
                  margin: '0 auto',
                  fontSize: {
                    xs: '0.9rem',
                    sm: 'clamp(0.9rem, 1.2vw, 1.5rem)',
                  },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                }}
              >
                <Box sx={sectionTextStyle}>
                  <Typography sx={detailLabelStyle}>Nickname:</Typography>
                  <Typography
                    sx={{ ...detailValueStyle, flex: 1, textAlign: 'left' }}
                  >
                    {fighter.nickname || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={sectionTextStyle}>
                  <Typography sx={detailLabelStyle}>GENDER:</Typography>
                  <Typography
                    sx={{ ...detailValueStyle, flex: 1, textAlign: 'left' }}
                  >
                    {capitalizeFirstLetter(fighter.gender)}
                  </Typography>
                </Box>
                <Box sx={sectionTextStyle}>
                  <Typography sx={detailLabelStyle}>DIVISION:</Typography>
                  <Typography
                    sx={{ ...detailValueStyle, flex: 1, textAlign: 'left' }}
                  >
                    {fight?.weight_class}
                  </Typography>
                </Box>
                <Box sx={sectionTextStyle}>
                  <Typography sx={detailLabelStyle}>AGE:</Typography>
                  <Typography
                    sx={{ ...detailValueStyle, flex: 1, textAlign: 'left' }}
                  >
                    {fighter.dob
                      ? calculateAge(fighter.dob.toString()).toString()
                      : 'N/A'}
                  </Typography>
                </Box>
                <Box sx={sectionTextStyle}>
                  <Typography sx={detailLabelStyle}>HEIGHT:</Typography>
                  <Typography
                    sx={{ ...detailValueStyle, flex: 1, textAlign: 'left' }}
                  >
                    {convertToFeetInches(fighter.height)}
                  </Typography>
                </Box>
                <Box sx={sectionTextStyle}>
                  <Typography sx={detailLabelStyle}>REACH:</Typography>
                  <Typography
                    sx={{ ...detailValueStyle, flex: 1, textAlign: 'left' }}
                  >
                    {convertToInches(fighter.reach)}"
                  </Typography>
                </Box>
                <Box sx={sectionTextStyle}>
                  <Typography sx={detailLabelStyle}>STANCE:</Typography>
                  <Typography
                    sx={{ ...detailValueStyle, flex: 1, textAlign: 'left' }}
                  >
                    {fighter.stance}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Upcoming Fight section - always displayed */}
            <Box sx={{ ...sectionContainerStyle }}>
              <SectionHeader title="Upcoming Fight" />
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  fontSize: {
                    xs: '0.8rem',
                    sm: 'clamp(0.8rem, 1.2vw, 1.2rem)',
                  },
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    ...sectionTextStyle,
                    textAlign: 'center',
                    width: '100%',
                    justifyContent: 'center',
                    display: 'flex',
                    fontSize: {
                      xs: '0.8rem',
                      sm: 'clamp(0.8rem, 1.2vw, 1rem)',
                    },
                  }}
                >
                  {seasonFight ? seasonFight.event_name : 'No Event Scheduled'}
                </Typography>
                <Typography
                  sx={{
                    ...sectionTextStyle,
                    textAlign: 'center',
                    width: '100%',
                    justifyContent: 'center',
                    display: 'flex',
                    fontSize: {
                      xs: '0.8rem',
                      sm: 'clamp(0.8rem, 1.2vw, 1rem)',
                    },
                  }}
                >
                  {seasonFight
                    ? `${fighterSeasonInfo.FightDate} v ${fighterSeasonInfo.Opponent}`
                    : 'No Fight Scheduled'}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                ...sectionContainerStyle,
                marginBottom: 0, // Remove margin from last section
              }}
            >
              <SectionHeader title="Current Form" />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: {
                    xs: '0.9rem',
                    sm: 'clamp(0.9rem, 1.2vw, 1.5rem)',
                  },
                  gap: '8px',
                  marginTop: '10px',
                }}
              >
                {recentFightResults?.map((result, index) => (
                  <FightResultCircle
                    key={index}
                    result={result.result}
                    isTitleFight={result.isTitleFight}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Slide>
  );
};

export default FighterDetails;
